import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import Icon from "../../components/common/Icon";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import DeleteModal from "../../components/modals/DeleteModal";
import EditBrandVoiceModal from "./brandVoices/EditBrandVoiceModal";
import { getBrandVoices, deleteBrandVoice, BrandVoice } from "../../api/brandVoice";
import { useNotification } from "../../context/NotificationContext";
import styles from "./brandVoices/BrandVoicesList.module.css";

const BrandVoicesList: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedBrandVoice, setSelectedBrandVoice] = useState<BrandVoice | null>(null);

    const fetchBrandVoices = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getBrandVoices();
            setBrandVoices(data);
        } catch (error: any) {
            console.error("Error fetching brand voices:", error);
            showNotification({
                message: t("brandVoices.fetchError") || "Failed to fetch brand voices",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [showNotification, t]);

    useEffect(() => {
        fetchBrandVoices();
    }, [fetchBrandVoices]);

    const handleEdit = (brandVoice: BrandVoice) => {
        setSelectedBrandVoice(brandVoice);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (brandVoice: BrandVoice) => {
        setSelectedBrandVoice(brandVoice);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedBrandVoice) return;

        try {
            await deleteBrandVoice(selectedBrandVoice.id);
            showNotification({
                message: t("brandVoices.deletedSuccessfully") || "Brand voice deleted successfully",
                type: "success",
            });
            await fetchBrandVoices();
        } catch (error: any) {
            console.error("Error deleting brand voice:", error);
            showNotification({
                message: t("brandVoices.deleteError") || "Failed to delete brand voice",
                type: "error",
            });
        } finally {
            setIsDeleteModalOpen(false);
            setSelectedBrandVoice(null);
        }
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setSelectedBrandVoice(null);
        fetchBrandVoices();
    };

    const canCreate = brandVoices.length < 10;
    const createTooltip = canCreate
        ? t("brandVoices.createNew") || "Créer une nouvelle voix"
        : t("brandVoices.maxReached") || "Maximum of 10 brand voices reached";

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHead}>
                <h1 className={styles.title}>{t("brandVoices.listTitle") || "Voix de marque"}</h1>
                <p className={styles.subtitle}>
                    {t("brandVoices.listSubtitle") ||
                        "Définissez les tons qui guident la rédaction de vos contenus. Chaque voix combine une description de style et des extraits de référence utilisés par l'IA."}
                </p>
            </div>

            <div className={styles.bar}>
                <h2 className={styles.barTitle}>
                    {t("brandVoices.yourVoices") || "Vos voix"}
                    <span className={styles.count}>{brandVoices.length}</span>
                </h2>
                <Tooltip title={createTooltip} arrow>
                    <span className={styles.newBtnWrap}>
                        <button
                            type="button"
                            className={styles.newBtn}
                            onClick={() => navigate("/resources/brand-voices/create")}
                            disabled={!canCreate}
                        >
                            <Icon name="ic-plus" />
                            {t("brandVoices.newVoice") || "Nouvelle voix"}
                        </button>
                    </span>
                </Tooltip>
            </div>

            {brandVoices.length === 0 ? (
                <div className={styles.empty}>
                    <p className={styles.emptyMessage}>{t("brandVoices.noBrandVoices") || "No brand voices yet"}</p>
                    <button
                        type="button"
                        className={styles.newBtn}
                        style={{ marginLeft: 0 }}
                        onClick={() => navigate("/resources/brand-voices/create")}
                        disabled={!canCreate}
                    >
                        <Icon name="ic-plus" />
                        {t("brandVoices.createFirst") || "Créer votre première voix"}
                    </button>
                </div>
            ) : (
                <div className={styles.list}>
                    {brandVoices.map((brandVoice) => (
                        <article key={brandVoice.id} className={styles.card}>
                            <div className={styles.cardTop}>
                                <h3 className={styles.cardName}>{brandVoice.name}</h3>
                                <div className={styles.cardActs}>
                                    <Tooltip title={t("edit")} arrow>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => handleEdit(brandVoice)}
                                            aria-label={t("edit")}
                                        >
                                            <EditIcon />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={t("delete")} arrow>
                                        <button
                                            type="button"
                                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                            onClick={() => handleDeleteClick(brandVoice)}
                                            aria-label={t("delete")}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                            {brandVoice.description && <p className={styles.cardDesc}>{brandVoice.description}</p>}
                            <div className={styles.cardFoot}>
                                <span>
                                    <Icon name="ic-file-text" />
                                    {brandVoice.excerpts?.length || 0} {t("brandVoices.excerptsCount") || "extraits"}
                                </span>
                                <span className={styles.sep} aria-hidden />
                                <span>
                                    <Icon name="ic-clock" />
                                    {t("brandVoices.createdAt") || "Créé"}{" "}
                                    {new Date(brandVoice.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedBrandVoice(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t("brandVoices.deleteTitle") || "Delete Brand Voice"}
                message={
                    selectedBrandVoice
                        ? t("brandVoices.deleteConfirmation", { name: selectedBrandVoice.name }) ||
                          `Are you sure you want to delete "${selectedBrandVoice.name}"? This action cannot be undone.`
                        : t("brandVoices.deleteConfirmation") || "Are you sure you want to delete this brand voice?"
                }
            />

            {selectedBrandVoice && (
                <EditBrandVoiceModal
                    isOpen={isEditModalOpen}
                    onClose={handleEditModalClose}
                    brandVoice={selectedBrandVoice}
                />
            )}
        </div>
    );
};

export default BrandVoicesList;
